import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo, { breadcrumbSchema } from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import CtaBand from "../components/CtaBand";

const JOURNEY = [
  ["Initial Enquiry", "It starts with a conversation — by phone, WhatsApp or the consultation form. We listen to what you want to achieve and ask a few straightforward questions about the property, your timescale and your plans."],
  ["Qualification Call", "We review every enquiry for suitability, location, budget and our current availability. If the project looks like a good fit for both sides, we arrange a site visit. If we are not the right builder for it, we will tell you honestly and point you in the right direction."],
  ["Site Assessment", "We visit the property, walk the space with you, and assess the practical realities: structure, access, drainage, services and anything the building is telling us. Good construction begins with proper investigation."],
  ["Design & Feasibility", "Where design input is needed, we coordinate concept drawings and test the feasibility of the scheme against your budget — because decisions are cheap on paper and expensive on site."],
  ["Planning or Permitted Development", "We confirm whether your project needs planning permission, falls within permitted development, or requires a lawful development certificate — and we prepare and submit applications where required."],
  ["Building Regulations", "We prepare or coordinate the technical drawings and manage the Building Regulations process, including building control inspections through construction."],
  ["Structural Engineering", "Any structural work — steels, openings, foundations — is designed by a qualified structural engineer and properly certified. No exceptions."],
  ["Specification & Quotation", "You receive a detailed, itemised proposal that reads like a description of the finished project: what is included, what is allowed for, and how changes would be handled. Not two lines and a number."],
  ["Pre-Construction Preparation", "Before work begins we agree the programme, site arrangements, working hours, access, protection for your home, and how we will communicate with you week to week."],
  ["Construction", "Our team and trusted trades deliver the work to the agreed specification, on a clean, safe and respectful site. One point of contact, answerable for everything."],
  ["Progress Communication", "You receive regular updates — what has been done, what happens next, and immediately if anything changes. You should never have to chase your builder for information."],
  ["Quality Inspections", "Work is inspected at key stages, by us and by building control where statutory inspections apply. Problems are caught early, when they are cheap to fix."],
  ["Completion", "A proper handover: a finished, cleaned project, all certificates and documentation, and a walkthrough to make sure everything is exactly as it should be."],
  ["Aftercare & Workmanship Guarantee", "Every NOOR project carries a 10-year workmanship guarantee. If something needs attention after handover, you know exactly who to call."],
];

export default function HowWeWork() {
  return (
    <div data-testid="how-we-work-page">
      <Seo
        title="How We Work | The NOOR Client Journey"
        description="From first conversation to completion and aftercare — how NOOR Design & Build plans, manages and delivers residential building projects."
        path="/how-we-work"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "How We Work", path: "/how-we-work" }])}
      />
      <section className="bg-graphite text-white blueprint-grid">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-16 md:pb-24">
          <Overline light>[ How We Work ]</Overline>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 max-w-3xl leading-[1.05]">
            Good construction begins long before anyone picks up a tool.
          </h1>
          <p className="text-white/60 text-base md:text-lg leading-relaxed mt-8 max-w-2xl">
            Proper investigation, clear scope definition and realistic planning are what separate a project that
            runs well from one that becomes a source of stress. This is the journey every NOOR client takes —
            though the exact services involved always depend on the individual project.
          </p>
        </div>
      </section>

      <section className="bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="border-t border-black/10">
            {JOURNEY.map(([title, text], i) => (
              <Reveal key={title}>
                <div className="grid lg:grid-cols-12 gap-4 lg:gap-8 py-10 md:py-14 border-b border-black/10" data-testid={`journey-step-${i}`}>
                  <div className="lg:col-span-2">
                    <p className="font-mono text-sm text-ember">[ {String(i + 1).padStart(2, "0")} ]</p>
                  </div>
                  <h2 className="lg:col-span-4 font-display font-light text-2xl md:text-3xl tracking-tight">{title}</h2>
                  <p className="lg:col-span-6 text-graphite/65 leading-relaxed text-sm md:text-base">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14">
            <div className="bg-stone border border-black/10 p-8 md:p-12">
              <p className="font-mono text-xs text-ember">[ A Note on Scope ]</p>
              <p className="text-graphite/70 leading-relaxed mt-4 max-w-3xl text-sm md:text-base">
                Not every project needs every stage. A like-for-like kitchen renovation will not need planning
                permission; a new-build home will need all of them. Part of our job is telling you honestly which
                steps apply to yours — and which do not.
              </p>
              <Link
                to="/request-a-consultation"
                data-testid="hww-consultation-button"
                className="group inline-flex items-center gap-3 mt-8 bg-graphite text-white px-8 py-4 text-sm font-semibold tracking-wide hover:bg-ember transition-colors duration-300"
              >
                Start the conversation <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
      <CtaBand />
    </div>
  );
}
